import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableFooter,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate } from 'react-router-dom';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Plus } from '../../assets';
import { UserContext } from '../../components/shared/useUser';
import { Section } from '../../components/ui/Section';
import { Pill } from '../../components/shared/Pill';
import { TableHeader } from '../../components/forms/TableHeader';
import {
  TaskSchedulesClient,
  TaskScheduleDto,
  TaskTemplatesClient,
  TaskTemplateDto,
  TaskCategoriesClient,
  TaskCategoryDto,
  TaskPrioritiesClient,
  TaskPriorityDto,
  TaskTemplateVersionDto,
} from '../../app/services/api/tasksClient';
import cronstrue from 'cronstrue';

const schedulesClient = TaskSchedulesClient;
const templatesClient = TaskTemplatesClient;
const categoriesClient = TaskCategoriesClient;
const prioritiesClient = TaskPrioritiesClient;

const TaskSchedulesPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);

  const canView = hasPermissionTo ? hasPermissionTo(['TaskSchedulesView']) : false;
  const canCreate = hasPermissionTo ? hasPermissionTo(['TaskSchedulesCreate']) : false;
  const canDelete = hasPermissionTo ? hasPermissionTo(['TaskSchedulesDelete']) : false;

  const [schedules, setSchedules] = useState<TaskScheduleDto[]>([]);
  const [templates, setTemplates] = useState<TaskTemplateDto[]>([]);
  const [categories, setCategories] = useState<TaskCategoryDto[]>([]);
  const [priorities, setPriorities] = useState<TaskPriorityDto[]>([]);

  const [searchText, setSearchText] = useState('');
  const [searchActiveOnly, setSearchActiveOnly] = useState(true);
  const [searchCategoryId, setSearchCategoryId] = useState<number | ''>('');
  const [searchPriorityId, setSearchPriorityId] = useState<number | ''>('');
  const [searchFromDate] = useState('');
  const [searchToDate] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [deleteTarget, setDeleteTarget] = useState<TaskScheduleDto | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!canView) return;
    schedulesClient.getSchedules().then(setSchedules).catch(() => setSchedules([]));
    templatesClient.getTemplates().then(setTemplates).catch(() => setTemplates([]));
    categoriesClient.getCategories().then(setCategories).catch(() => setCategories([]));
    prioritiesClient.getPriorities().then(setPriorities).catch(() => setPriorities([]));
  }, [canView]);

  const getActiveTemplateVersion = (template: TaskTemplateDto | undefined): TaskTemplateVersionDto | undefined => {
    if (!template) return undefined;
    return template.versions.find(v => v.isActive) ?? template.versions[0];
  };

  const getFrequencyDescription = (s: TaskScheduleDto) => {
    if (!s.cronExpression) return '';

    try {
      return cronstrue.toString(s.cronExpression, { use24HourTimeFormat: true });
    } catch {
      return s.cronExpression;
    }
  };

  const filteredSchedules = useMemo(() => {
    return schedules.filter(s => {
      if (searchActiveOnly && !s.isActive) return false;
      if (searchText.trim()) {
        const text = searchText.toLowerCase();
        const name = (s.taskScheduleName ?? '').toLowerCase();
        const templateName = getTemplateName(s.taskTemplateId).toLowerCase();
        if (!name.includes(text) && !templateName.includes(text)) return false;
      }
      if (searchCategoryId && s.templateTask.taskCategoryId !== Number(searchCategoryId)) return false;
      if (searchPriorityId && s.templateTask.versions.find(v => v.isActive)?.defaultTaskPriorityId !== Number(searchPriorityId)) return false;
      return true;
    });
  }, [schedules, searchActiveOnly, searchText, searchCategoryId, searchPriorityId, searchFromDate, searchToDate, templates]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const pagedSchedules = useMemo(
    () => filteredSchedules.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredSchedules, page, rowsPerPage],
  );

  const getTemplateName = (id: number) => templates.find(t => t.id === id)?.name ?? '';

  const getCategoryNameForSchedule = (s: TaskScheduleDto) => {
    if (s.templateTask?.taskCategoryId) {
      const direct = categories.find(c => c.id === s.templateTask.taskCategoryId)?.name;
      if (direct) return direct;
    }
    const template = templates.find(t => t.id === s.taskTemplateId);
    const categoryIdFromTemplate = template?.taskCategoryId;
    return categories.find(c => c.id === categoryIdFromTemplate)?.name ?? '';
  };

  const getPriorityNameForSchedule = (s: TaskScheduleDto) => {
    if (s.templateTask?.versions?.find(v => v.isActive)?.defaultTaskPriorityId) {
      const direct = priorities.find(p => p.id === s.templateTask.versions.find(v => v.isActive)?.defaultTaskPriorityId)?.name;
      if (direct) return direct;
    }
    const template = templates.find(t => t.id === s.taskTemplateId);
    const activeVersion = getActiveTemplateVersion(template);
    const priorityIdFromTemplate = activeVersion?.defaultTaskPriorityId;
    return priorities.find(p => p.id === priorityIdFromTemplate)?.name ?? '';
  };

  const getScheduleName = (s: TaskScheduleDto) => s.taskScheduleName ?? '';

  const handleOpenCreate = () => {
    if (!canCreate) return;
    navigate('/task-schedules/new');
  };

  const handleOpenEdit = (row: TaskScheduleDto) => {
    if (!canView) return;
    navigate(`/task-schedules/${row.id}`);
  };

  const handleRequestDelete = (row: TaskScheduleDto, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canDelete) return;
    setDeleteTarget(row);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await schedulesClient.deleteSchedule(deleteTarget.id);
      setSchedules(prev => prev.filter(s => s.id !== deleteTarget.id));
      setDeleteTarget(null);
      setDeleteConfirmOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteTarget(null);
    setDeleteConfirmOpen(false);
  };

  if (!canView) {
    return (
      <PageArea>
        <PageTitle>Task Schedules</PageTitle>
        <Pill text='You do not have permission to view task schedules.' />
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Grid container
        spacing={5}>
        <Grid item
          xs={12}>
          <PageTitle>Task Schedules</PageTitle>
        </Grid>

        <Grid item
          xs={12}
          md={9}>
          <Grid container
            spacing={2}>
            <Grid item
              xs={12}
              md={3}>
              <TextField
                label='Search'
                fullWidth
                size='small'
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder='Search by name or template'
              />
            </Grid>
            <Grid item
              xs={12}
              md={3}>
              <TextField
                select
                label='Category'
                fullWidth
                size='small'
                value={searchCategoryId}
                onChange={e => setSearchCategoryId(e.target.value === '' ? '' : Number(e.target.value))}
              >
                <MenuItem value=''>All</MenuItem>
                {categories.map(c => (
                  <MenuItem key={c.id}
                    value={c.id}>{c.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              xs={12}
              md={3}>
              <TextField
                select
                label='Priority'
                fullWidth
                size='small'
                value={searchPriorityId}
                onChange={e => setSearchPriorityId(e.target.value === '' ? '' : Number(e.target.value))}
              >
                <MenuItem value=''>All</MenuItem>
                {priorities.map(p => (
                  <MenuItem key={p.id}
                    value={p.id}>{p.name}</MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item
              xs={12}
              md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={searchActiveOnly}
                    onChange={e => setSearchActiveOnly(e.target.checked)}
                  />
                }
                label='Active only'
              />
            </Grid>

          </Grid>
        </Grid>

        <Grid item
          xs={12}
          md={3}
          sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
          {canCreate && (
            <Button
              size='lg'
              variant='primary'
              startIcon={<Plus />}
              onClick={handleOpenCreate}
            >
              Create Schedule
            </Button>
          )}
        </Grid>

        <Grid item
          xs={12}>
          <Section>
            <TableHeader>Task Schedules</TableHeader>
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Frequency</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Start</TableCell>
                    <TableCell>End</TableCell>
                    <TableCell align='right'></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedSchedules.map(row => (
                    <TableRow
                      key={row.id}
                      hover
                      onClick={() => handleOpenEdit(row)}
                    >
                      <TableCell>{getScheduleName(row)}</TableCell>
                      <TableCell>{getFrequencyDescription(row)}</TableCell>
                      <TableCell>{getCategoryNameForSchedule(row)}</TableCell>
                      <TableCell>{getPriorityNameForSchedule(row)}</TableCell>
                      <TableCell>{row.startDate}</TableCell>
                      <TableCell>{row.endDate}</TableCell>
                      <TableCell align='right'
                        onClick={e => e.stopPropagation()}>
                        {canDelete && (
                          <DeleteOutlineIcon
                            fontSize='small'
                            color='error'
                            onClick={e => handleRequestDelete(row, e)}
                            style={{ cursor: 'pointer' }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {pagedSchedules.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9}
                        style={{ textAlign: 'center' }}>
                        No schedules found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25]}
                      count={filteredSchedules.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Section>
        </Grid>
      </Grid>

      {deleteTarget && (
        <Dialog
          open={deleteConfirmOpen}
          onClose={handleCancelDelete}
        >
          <DialogTitle>Delete task schedule?</DialogTitle>
          <DialogContent>
            {`Are you sure you want to delete the schedule "${getScheduleName(deleteTarget)}"? This action cannot be undone.`}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDelete}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant='primary'
              color='error'
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}

    </PageArea>
  );
};

export default TaskSchedulesPage;
